import { useEffect, useState } from 'react';
import {
  CheckCircle,
  KeyboardArrowDown,
  KeyboardArrowLeft,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Container,
  FormGroup,
  Grid,
  MenuItem,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import Form from 'components/Form';
import { getSignupCountries } from 'apis/account';
import { ISignupCountryOption } from 'apis/account/type';
import SignUpDialog from 'routes/SignUp/SignUpDialog';
import { IFormValues } from 'routes/Main/type';
import { toBirthDecade } from 'constants/signup';
import { getClassSection } from 'apis/account';
import useUser from 'store/user/useUser';
import { getClassSectionList } from 'apis/main';

interface Props extends IFormValues {
  studentGrade: number;
  studentGender: number;
  collegeCountryId?: number;
  collegeCountryName?: string;
  register: UseFormRegister<FieldValues>;
  isConfirmPassword: boolean;
  onConfirmPassword: () => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function MyPageForm(props: Props) {
  const [countryOptions, setCountryOptions] = useState<ISignupCountryOption[]>(
    []
  );
  const [isTerms, setIsTerms] = useState(false);
  const [isPersonal, setIsPersonal] = useState(false);
  const [classSections, setClassSections] = useState<any>([]);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { student_uuid: user_id } = useUser();
  const {
    studentGrade,
    studentGender,
    collegeCountryId,
    collegeCountryName,
    register,
    isConfirmPassword,
    onConfirmPassword,
    getValues,
    setValue,
  } = props;
  const gradeForDisplay = studentGrade === 5 ? 2 : studentGrade;

  useEffect(() => {
    getSignupCountries().then(({ data }) => {
      if (data.rc === 1) {
        setCountryOptions(data.countries ?? []);
      }
    });
  }, []);

  const college_id = getValues('student_college');

  // 초기 분반 데이터 로드
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        // 사용자의 현재 분반 정보 가져오기
        if (user_id) {
          const request = {
            page: 1,
            user_id,
            size: 10,
          };
          const userSectionsRes = await getClassSectionList(request);
          const userSections = userSectionsRes.data.class_section_list || [];
          const userSectionIds = userSections.map(
            (section: any) => section.class_section_id
          );
          setSelectedSections(userSectionIds);
          setValue('sign_sx', userSectionIds); // form 값 설정
        }

        // 선택 가능한 전체 분반 목록 가져오기
        if (college_id) {
          const sectionsRes = await getClassSection(college_id);
          setClassSections(sectionsRes.data.class_section_list);
        }
      } catch (error) {
        console.error('Error loading class sections:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [college_id, setValue]);

  const VerificationIcon = () => (
    <CheckCircle
      fontSize="small"
      color="primary"
      sx={{ display: isConfirmPassword ? 'block' : 'none' }}
    />
  );

  // 정확한 연도로 저장된 옛 값도 년대로 내려 보여준다
  const birth = toBirthDecade(getValues('student_birth'));

  const [이름, set이름] = useState('');

  const nameRegex = () => {
    let check = /@/;
    return check.test(이름);
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    setSelectedSections(selectedValues);
    setValue('sign_sx', selectedValues); // form 값 업데이트
  };

  const handleDelete = (e: React.MouseEvent, valueToDelete: string) => {
    e.preventDefault();
    const newSelectedSections = selectedSections.filter(
      value => value !== valueToDelete
    );

    setSelectedSections(newSelectedSections);
    setValue('sign_sx', newSelectedSections); // form 값 업데이트
  };

  const getSectionText = (sectionId: string) => {
    const section = classSections.find(
      (section: any) => section.class_section_id === sectionId
    );
    if (!section) return sectionId;

    return `${section.class_section_year} - ${section.class_section_semester} / ${section.class_section_subject} / ${section.class_section_grade}학년 / ${section.class_section_name} / ${section.class_section_teacher_info}`;
  };

  return (
    <Box>
      <Container maxWidth="sm" sx={{ mt: 7.5, mb: 6 }}>
        <Button
          href="/#/"
          size="large"
          color="inherit"
          startIcon={<KeyboardArrowLeft />}
          sx={{ mb: 5, p: 0, backgroundColor: '#fff', color: 'black' }}
        >
          계정 설정 및 수정
        </Button>

        <Stack spacing={2.5}>
          <Form.Item label="이메일">
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                disabled={true}
                {...register('student_id')}
                InputProps={{ readOnly: true }}
              />
            </Box>
          </Form.Item>
          <Form.Item label="기존 비밀번호 입력">
            <Box display="flex" gap={1}>
              <Form.Password
                required
                fullWidth
                helperText="계정 정보 수정을 위해 비밀번호를 확인합니다.
                기존 비밀번호 입력 후 확인을 눌러주세요."
                sx={{ whiteSpace: 'nowrap' }}
                InputProps={{
                  readOnly: isConfirmPassword,
                  endAdornment: <VerificationIcon />,
                }}
                {...register('password')}
              />
              <Button
                variant={'contained'}
                onClick={onConfirmPassword}
                sx={{
                  width: 150,
                  fontSize: 16,
                  maxHeight: 56,
                  whiteSpace: 'nowrap',
                  display: isConfirmPassword ? 'none' : 'block',
                }}
              >
                확인
              </Button>
            </Box>
          </Form.Item>

          <Form.Item label="새 비밀번호 입력">
            <Form.Password
              required={false}
              fullWidth
              helperText="비밀번호를 변경하는 경우에만 입력해 주세요 (영문+숫자+특수기호 포함 8자리 이상)"
              {...register('newPassword')}
            />
          </Form.Item>
          <Form.Item label="새 비밀번호 확인">
            <Form.Password
              required={false}
              fullWidth
              isHideVisibleBtn
              {...register('newPasswordConfirm')}
            />
          </Form.Item>

          <Form.Item label="이름">
            <TextField
              required
              fullWidth
              helperText={
                nameRegex()
                  ? '이메일 주소를 작성하지 않도록 주의해 주세요.'
                  : ''
              }
              color={nameRegex() ? 'warning' : 'success'}
              {...register('student_name', {
                onChange: e => set이름(e.target.value),
              })}
            />
          </Form.Item>
          <Grid container>
            <Grid item xs={6}>
              <Form.Item label="성별">
                <Form.MuiRadioGroup
                  i18nKey="GENDER"
                  disabled={true}
                  values={[1, 2]}
                  value={studentGender}
                  defaultValue={studentGender}
                />
              </Form.Item>
            </Grid>
            <Grid item xs={6}>
              <Form.Item label="구분">
                <Form.MuiRadioGroup
                  disabled={true}
                  i18nKey="GRADE"
                  values={[1, 2]}
                  value={gradeForDisplay}
                  defaultValue={gradeForDisplay}
                />
              </Form.Item>
            </Grid>
          </Grid>

          <Form.Item label="국적 선택">
            {collegeCountryId ? (
              <TextField
                select
                fullWidth
                disabled
                value={collegeCountryId}
                InputProps={{
                  readOnly: true,
                  endAdornment: <KeyboardArrowDown />,
                }}
              >
                {countryOptions.map(({ college_country_id, college_country_name }) => (
                  <MenuItem key={college_country_id} value={college_country_id}>
                    {college_country_name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                disabled
                fullWidth
                value={collegeCountryName ?? '-'}
                InputProps={{ readOnly: true }}
              />
            )}
          </Form.Item>

          <Form.Item label="학교 선택">
            <TextField
              disabled={true}
              fullWidth
              {...register('college_name')}
              InputProps={{
                readOnly: true,
                endAdornment: <KeyboardArrowDown />,
              }}
            />
          </Form.Item>
          <Form.Item label="학번/사번">
            <TextField
              disabled={true}
              fullWidth
              InputProps={{ readOnly: true }}
              {...register('student_no')}
            />
          </Form.Item>
          <Form.Item label="출생년대">
            <TextField
              disabled={true}
              fullWidth
              // {...register('student_birth')}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              defaultValue={birth}
            />
          </Form.Item>
          {studentGrade === 1 ? (
            <Form.Item label="분반">
              <Select
                labelId="demo-multiple-chip-label"
                id="demo-multiple-chip"
                fullWidth
                multiple
                value={selectedSections}
                onChange={handleChange}
                input={<OutlinedInput id="select-multiple-chip" />}
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(value => (
                      <Chip
                        key={value}
                        label={getSectionText(value)}
                        onMouseDown={event => {
                          event.stopPropagation();
                        }}
                        onDelete={e => handleDelete(e, value)}
                        style={{
                          borderRadius: '5px',
                          backgroundColor: 'white',
                          border: '1px solid lightgray',
                          color: '#0000004b',
                          maxWidth: '100%',
                        }}
                        sx={{
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {classSections.map((section: any) => {
                  const sectionText = `${section.class_section_year} - ${section.class_section_semester} / ${section.class_section_subject} / ${section.class_section_grade}학년 / ${section.class_section_name} / ${section.class_section_teacher_info}`;
                  return (
                    <MenuItem
                      key={section.class_section_id}
                      value={section.class_section_id}
                    >
                      {sectionText}
                    </MenuItem>
                  );
                })}
              </Select>
            </Form.Item>
          ) : null}

          <FormGroup sx={{ mt: 2.5 }}>
            <Typography
              gutterBottom
              variant="body2"
              onClick={() => setIsTerms(true)}
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              스마트널스 서비스 이용약관
            </Typography>
            <Typography
              variant="body2"
              onClick={() => setIsPersonal(true)}
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              개인정보 수집 및 이용약관
            </Typography>
          </FormGroup>
          <Form.Item>
            <Button fullWidth size="large" type="submit" variant="contained">
              정보 수정
            </Button>
          </Form.Item>
        </Stack>
      </Container>

      <SignUpDialog.TermsOfService
        isOpen={isTerms}
        onClose={() => setIsTerms(false)}
      />

      <SignUpDialog.PersonalInfo
        isOpen={isPersonal}
        onClose={() => setIsPersonal(false)}
      />
    </Box>
  );
}

export default MyPageForm;
